import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, Download, FileCheck2, AlertCircle, CheckCircle2, X } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { downloadBlob } from "@/lib/download";
import { fmtHours } from "@/foundation/formatting";

export default function EmployeeImportDialog({ open, onOpenChange, companyId, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // {valid, invalid, valid_count, invalid_count}
  const [step, setStep] = useState(1); // 1: upload, 2: preview, 3: done
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [skipDup, setSkipDup] = useState(true);

  const reset = () => { setFile(null); setPreview(null); setStep(1); setResult(null); };

  const closeDialog = () => { reset(); onOpenChange(false); };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/employees/import/template.xlsx", { responseType: "blob" });
      downloadBlob(res.data, "template_dipendenti.xlsx", res.headers?.["content-type"]);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const runPreview = async () => {
    if (!file) return toast.error("Seleziona un file Excel");
    if (!companyId) return toast.error("Seleziona prima un'azienda");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/employees/import/preview?company_id=${companyId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setStep(2);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  const runConfirm = async () => {
    if (!preview?.valid?.length) return toast.error("Nessuna riga valida da importare");
    setLoading(true);
    try {
      const rows = preview.valid.map((r) => r.payload);
      const { data } = await api.post("/employees/import/confirm", {
        company_id: companyId, rows, skip_duplicates: skipDup,
      });
      setResult(data);
      setStep(3);
      toast.success(`${data.created_count} dipendenti importati`);
      if (onImported) onImported();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent formDialog className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="employee-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload size={18} /> Importa dipendenti da Excel
          </DialogTitle>
          <DialogDescription>
            {step === 1 && "Carica un file .xlsx con le colonne del template. Nessun dato verrà scritto in DB in questa fase."}
            {step === 2 && "Controlla l'anteprima e conferma per procedere con l'inserimento nel database."}
            {step === 3 && "Import completato."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          {/* STEP 1: upload */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-4 flex items-start gap-3">
                <FileCheck2 size={18} className="text-emerald-700 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-emerald-900">Scarica prima il template</div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    Compila le colonne indicate (Cognome e Nome obbligatori). Formato date: AAAA-MM-GG o GG/MM/AAAA.
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template-btn">
                  <Download size={14} className="mr-1" /> Template
                </Button>
              </div>

              <div className="border-2 border-dashed border-slate-300 rounded-md p-6 text-center">
                <input
                  type="file"
                  id="employee-import-file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="hidden"
                  data-testid="employee-import-file-input"
                />
                <label htmlFor="employee-import-file" className="cursor-pointer">
                  <Upload size={32} className="mx-auto text-slate-400" />
                  <div className="mt-2 text-sm font-medium text-slate-700">
                    {file ? file.name : "Clicca per selezionare un file Excel"}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">.xlsx o .xls, max 5 MB</div>
                </label>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={closeDialog}>Annulla</Button>
                <Button onClick={runPreview} disabled={!file || loading}
                        className="bg-emerald-800 hover:bg-emerald-700 text-white"
                        data-testid="import-preview-btn">
                  {loading ? "Analisi…" : "Analizza file"}
                </Button>
              </div>
            </div>
          )}

          {/* STEP 2: preview */}
          {step === 2 && preview && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="border border-emerald-200 bg-emerald-50/50 rounded-md p-3">
                  <div className="text-xs uppercase tracking-wider text-emerald-800">Righe valide</div>
                  <div className="text-2xl font-mono font-bold text-emerald-900">{preview.valid_count}</div>
                </div>
                <div className="border border-red-200 bg-red-50/50 rounded-md p-3">
                  <div className="text-xs uppercase tracking-wider text-red-800">Righe invalide</div>
                  <div className="text-2xl font-mono font-bold text-red-900">{preview.invalid_count}</div>
                </div>
                <div className="border border-amber-200 bg-amber-50/50 rounded-md p-3">
                  <div className="text-xs uppercase tracking-wider text-amber-800">Con warning</div>
                  <div className="text-2xl font-mono font-bold text-amber-900">
                    {preview.valid.filter((r) => r.warnings?.length > 0).length}
                  </div>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={skipDup} onChange={(e) => setSkipDup(e.target.checked)}
                       data-testid="skip-duplicates-checkbox" />
                Salta i dipendenti con matricola già esistente
              </label>

              {preview.invalid_count > 0 && (
                <div className="border border-red-200 rounded-md">
                  <div className="px-3 py-2 bg-red-50 text-sm font-semibold text-red-900 flex items-center gap-2">
                    <AlertCircle size={16} /> Righe con errori (non saranno importate)
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16 text-xs">Riga</TableHead>
                        <TableHead className="text-xs">Errori</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.invalid.map((r) => (
                        <TableRow key={r.row}>
                          <TableCell className="font-mono">{r.row}</TableCell>
                          <TableCell className="text-xs text-red-800">
                            {r.errors.join("; ")}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {preview.valid_count > 0 && (
                <div className="border border-slate-200 rounded-md">
                  <div className="px-3 py-2 bg-emerald-50/30 text-sm font-semibold text-emerald-900">
                    Anteprima righe valide ({preview.valid_count})
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Cognome</TableHead>
                          <TableHead className="text-xs">Nome</TableHead>
                          <TableHead className="text-xs">Matricola</TableHead>
                          <TableHead className="text-xs">Tipo</TableHead>
                          <TableHead className="text-xs text-right">Saldo iniz.</TableHead>
                          <TableHead className="text-xs">Warning</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {preview.valid.map((r) => (
                          <TableRow key={r.row}>
                            <TableCell className="font-semibold">{r.payload.last_name}</TableCell>
                            <TableCell>{r.payload.first_name}</TableCell>
                            <TableCell className="font-mono text-xs">{r.payload.matricola || "—"}</TableCell>
                            <TableCell className="text-xs">{r.payload.employment_type || "full_time"}</TableCell>
                            <TableCell className="text-right font-mono">
                              {fmtHours(Number(r.payload.initial_balance_hours) || 0)} h
                            </TableCell>
                            <TableCell>
                              {r.warnings?.length ? (
                                <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[10px]">
                                  {r.warnings.join(", ")}
                                </Badge>
                              ) : "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setStep(1)}>Indietro</Button>
                <Button onClick={runConfirm} disabled={!preview.valid_count || loading}
                        className="bg-emerald-800 hover:bg-emerald-700 text-white"
                        data-testid="import-confirm-btn">
                  {loading ? "Importazione…" : `Importa ${preview.valid_count} dipendenti`}
                </Button>
              </div>
            </div>
          )}

          {/* STEP 3: result */}
          {step === 3 && result && (
            <div className="space-y-4">
              <div className="border border-emerald-300 bg-emerald-50 rounded-md p-4 text-center">
                <CheckCircle2 size={40} className="mx-auto text-emerald-700" />
                <div className="text-lg font-semibold text-emerald-900 mt-2">Import completato</div>
                <div className="text-sm text-slate-700 mt-1">
                  <b>{result.created_count}</b> dipendenti creati
                  {result.skipped_count > 0 && <>, {result.skipped_count} saltati (duplicati)</>}
                  {result.error_count > 0 && <>, {result.error_count} errori</>}
                </div>
              </div>

              {result.errors?.length > 0 && (
                <div className="border border-red-200 rounded-md">
                  <div className="px-3 py-2 bg-red-50 text-sm font-semibold text-red-900">Errori inserimento</div>
                  <div className="max-h-40 overflow-y-auto">
                    {result.errors.map((e, i) => (
                      <div key={i} className="px-3 py-2 text-xs border-t border-red-100">
                        <span className="font-semibold">{e.payload?.last_name} {e.payload?.first_name}:</span> {e.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button onClick={closeDialog} className="bg-emerald-800 hover:bg-emerald-700 text-white"
                        data-testid="import-close-btn">
                  <X size={14} className="mr-1" /> Chiudi
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
